/**
 * Employer-side statutory contributions on top of a Finnish gross salary, 2026.
 *
 * These are costs the employer pays in addition to gross salary, on top of the
 * employee-side contributions already modeled in `calculate-finnish-net-salary.ts`.
 * An employee never sees these on a payslip, but they are real cost of employing
 * them and are the core "hidden" layer of the tax-iceberg calculator.
 *
 * Parameter sources (2026):
 * - TyEL employer share (17.10%, flat across ages since the 2026 reform removed
 *   age-banding): Eläketurvakeskus, https://www.etk.fi/ajankohtaista/tyoelakemaksut-vuonna-2026/
 *   (total TyEL contribution is 24.4%; employee share is the flat 7.30% already in
 *   `TAX_PARAMS_2026.pensionRate`, so employer share = total - employee share).
 * - Employer unemployment insurance (työttömyysvakuutusmaksu): tiered by the
 *   employer's total annual payroll — 0.31% up to the threshold, 1.23% above it,
 *   commonly cited blended average 0.92%. Source: Työllisyysrahasto 2026 announcement,
 *   https://www.tyollisyysrahasto.fi/uutiset/vuoden-2026-tyottomyysvakuutusmaksut-on-vahvistettu/
 * - Employer health insurance contribution (sairausvakuutuksen työnantajamaksu):
 *   1.91% for 2026. Source: Vero.fi, https://www.vero.fi/syventavat-vero-ohjeet/ohje-hakusivu/338440/tyonantajan-sairausvakuutusmaksu2/
 * - Occupational accident + occupational-disease insurance and group life insurance:
 *   these are NOT statutory flat rates — accident insurance is priced per employer by
 *   industry risk class (roughly 0.05%-5%) and group life insurance is a small addition
 *   to that. The figures below are TVK's (Tapaturmavakuutuskeskus) published annual
 *   AVERAGE forecast across all employers, not a rate any specific employer actually
 *   pays. TODO(verify): the direct TVK source page (tyotapaturmatieto.fi) returned a
 *   TLS error when this was researched; the 0.70% / 0.06% figures are corroborated by
 *   secondary payroll-guide sources referencing TVK but should be re-confirmed against
 *   TVK's own publication before this is presented as a hard "sourced" number rather
 *   than an estimate.
 *
 * Model notes:
 * - `employerSize` selects the unemployment-insurance tier. Most job seekers don't know
 *   their prospective employer's total payroll, so this defaults to the blended average
 *   when omitted — same "generic estimate" spirit as `calculate-finnish-net-salary.ts`'s
 *   municipal-tax fallback.
 * - Accident + group life insurance are always the average estimate; there is no
 *   reliable public per-industry rate table to offer a more precise toggle for.
 */

export interface EmployerCostParams {
  year: number;
  tyelEmployerRate: number;
  unemploymentInsuranceLowRate: number; // below the payroll threshold
  unemploymentInsuranceHighRate: number; // above the payroll threshold
  unemploymentInsuranceBlendedRate: number; // used when employer size is unknown
  unemploymentInsurancePayrollThreshold: number; // EUR/year
  healthInsuranceRate: number;
  accidentInsuranceAvgRate: number;
  groupLifeInsuranceAvgRate: number;
}

export const EMPLOYER_COST_PARAMS_2026: EmployerCostParams = {
  year: 2026,
  tyelEmployerRate: 0.171,
  unemploymentInsuranceLowRate: 0.0031,
  unemploymentInsuranceHighRate: 0.0123,
  unemploymentInsuranceBlendedRate: 0.0092,
  unemploymentInsurancePayrollThreshold: 2_509_500,
  healthInsuranceRate: 0.0191,
  accidentInsuranceAvgRate: 0.007,
  groupLifeInsuranceAvgRate: 0.0006,
};

export type EmployerSize = 'small' | 'large';

export interface EmployerCostInput {
  grossAnnual: number;
  /** Total annual payroll size of the employer, if known. Affects only unemployment insurance. */
  employerSize?: EmployerSize;
  params?: EmployerCostParams;
}

export interface EmployerCostResult {
  tyel: number;
  unemploymentInsurance: number;
  healthInsurance: number;
  accidentInsurance: number;
  groupLife: number;
  total: number;
  /** True when employerSize was not provided and the blended average rate was used. */
  isGenericUnemploymentInsuranceEstimate: boolean;
}

export function calculateEmployerCost(input: EmployerCostInput): EmployerCostResult {
  const p = input.params ?? EMPLOYER_COST_PARAMS_2026;
  const gross = input.grossAnnual;
  if (!(gross > 0)) throw new Error('Provide a positive grossAnnual');

  const unemploymentInsuranceRate =
    input.employerSize === undefined
      ? p.unemploymentInsuranceBlendedRate
      : input.employerSize === 'large'
        ? p.unemploymentInsuranceHighRate
        : p.unemploymentInsuranceLowRate;

  const tyel = gross * p.tyelEmployerRate;
  const unemploymentInsurance = gross * unemploymentInsuranceRate;
  const healthInsurance = gross * p.healthInsuranceRate;
  const accidentInsurance = gross * p.accidentInsuranceAvgRate;
  const groupLife = gross * p.groupLifeInsuranceAvgRate;
  const total = tyel + unemploymentInsurance + healthInsurance + accidentInsurance + groupLife;

  const r = (x: number) => Math.round(x * 100) / 100;
  return {
    tyel: r(tyel),
    unemploymentInsurance: r(unemploymentInsurance),
    healthInsurance: r(healthInsurance),
    accidentInsurance: r(accidentInsurance),
    groupLife: r(groupLife),
    total: r(total),
    isGenericUnemploymentInsuranceEstimate: input.employerSize === undefined,
  };
}
